/**
 * Permissions and Role-Based Access Control Utilities
 *
 * This module provides utilities for:
 * - Getting current user from localStorage
 * - Checking user permissions based on their role
 * - Filtering data based on data_access levels
 */

import { getClientCookie } from '@/utils/cookies';

// Role name constants
export const ROLES = {
  SUPER_ADMIN: 'SUPER_ADMIN',
  SYSTEM_ADMIN: 'SYSTEM_ADMIN',
  SALES_DIRECTOR: 'SALES_DIRECTOR',
  SALES_MANAGER: 'SALES_MANAGER',
  TEAM_LEAD: 'TEAM_LEAD',
  SENIOR_SALES_REP: 'SENIOR_SALES_REP',
  SALES_REP: 'SALES_REP',
  JUNIOR_SALES_REP: 'JUNIOR_SALES_REP',
  SUPPORT_MANAGER: 'SUPPORT_MANAGER',
  READ_ONLY: 'READ_ONLY'
} as const;

// Data access levels
export const DATA_ACCESS = {
  ALL: 'all',
  TEAM: 'team',
  DEPARTMENT: 'department',
  ASSIGNED: 'assigned',
  OWN: 'own'
} as const;

// Permission keys
export const PERMISSIONS = {
  CAN_CREATE_LEADS: 'can_create_leads',
  CAN_EDIT_LEADS: 'can_edit_leads',
  CAN_DELETE_LEADS: 'can_delete_leads',
  CAN_CREATE_ACTIVITIES: 'can_create_activities',
  CAN_EDIT_ACTIVITIES: 'can_edit_activities',
  CAN_DELETE_ACTIVITIES: 'can_delete_activities',
  CAN_VIEW_REPORTS: 'can_view_reports',
  CAN_MANAGE_USERS: 'can_manage_users',
  CAN_MANAGE_TEAMS: 'can_manage_teams',
  CAN_MANAGE_SETTINGS: 'can_manage_settings'
} as const;

// User interface
export interface CRMUser {
  userId?: number;
  uuid?: string;
  userName?: string;
  userEmail?: string;
  client_id_fk?: number;
  role_id_fk?: number;
  role?: {
    role_id: number;
    role_name: string;
    description: string;
    data_access: 'all' | 'team' | 'department' | 'assigned' | 'own';
    permissions: Record<string, boolean>;
  };
  team_id_fk?: number;
  department_id_fk?: number;
}

/**
 * Get the current user from cookies (using AuthData utility)
 */
export const getCurrentUser = (): CRMUser | null => {
  try {
    // First try to get from compressed cookie
    const userData = getClientCookie({
      name: "crm_user_data",
      isParse: true,
      isCompress: true,
    });

    if (userData) {
      return userData as CRMUser;
    }

    // Fallback to localStorage for backwards compatibility
    const userJson = localStorage.getItem('crm_user');
    if (!userJson) return null;

    const user = JSON.parse(userJson);
    return user;
  } catch (error) {
    console.error('Error getting user data:', error);
    return null;
  }
};

/**
 * Get the current user's role name
 */
export const getCurrentUserRole = (): string | null => {
  const user = getCurrentUser();
  return user?.role?.role_name || null;
};

/**
 * Get the current user's data access level
 */
export const getDataAccess = (): string => {
  const user = getCurrentUser();

  // If no role data available, default to ALL for backward compatibility
  // This ensures Super Admins without proper role structure still have full access
  if (!user?.role?.data_access) {
    console.warn('No role data_access found, defaulting to ALL');
    return DATA_ACCESS.ALL;
  }

  return user.role.data_access;
};

/**
 * Check if the current user has a specific permission
 */
export const hasPermission = (permissionKey: string): boolean => {
  const user = getCurrentUser();

  // If no role permissions structure, default to true for backward compatibility
  // This ensures users without proper role structure still have access
  if (!user?.role?.permissions) {
    console.warn('No role permissions found, defaulting to true for permission:', permissionKey);
    return true;
  }

  return user.role.permissions[permissionKey] === true;
};

/**
 * Check if the current user can create activities/events
 */
export const canCreateActivities = (): boolean => {
  return hasPermission(PERMISSIONS.CAN_CREATE_ACTIVITIES);
};

/**
 * Check if the current user can edit activities/events
 */
export const canEditActivities = (): boolean => {
  return hasPermission(PERMISSIONS.CAN_EDIT_ACTIVITIES);
};

/**
 * Check if the current user can delete activities/events
 */
export const canDeleteActivities = (): boolean => {
  return hasPermission(PERMISSIONS.CAN_DELETE_ACTIVITIES);
};

/**
 * Check if a user is visible based on data access level
 * @param targetUserId - The ID of the user to check
 * @param targetUserTeamId - The team ID of the user to check
 * @param targetUserDepartmentId - The department ID of the user to check
 */
export const canViewUser = (
  targetUserId: number | string,
  targetUserTeamId?: number | string,
  targetUserDepartmentId?: number | string
): boolean => {
  const currentUser = getCurrentUser();
  if (!currentUser) return false;

  const dataAccess = getDataAccess();

  switch (dataAccess) {
    case DATA_ACCESS.ALL:
      return true;

    case DATA_ACCESS.TEAM:
      // Can see users in the same team
      if (!currentUser.team_id_fk || !targetUserTeamId) return false;
      return currentUser.team_id_fk === Number(targetUserTeamId);

    case DATA_ACCESS.DEPARTMENT:
      // Can see users in the same department
      if (!currentUser.department_id_fk || !targetUserDepartmentId) return false;
      return currentUser.department_id_fk === Number(targetUserDepartmentId);

    case DATA_ACCESS.OWN:
      // Can only see themselves
      return currentUser.userId === Number(targetUserId) ||
             currentUser.uuid === targetUserId;

    case DATA_ACCESS.ASSIGNED:
      // For now, treat assigned similar to own
      // In a real implementation, you'd check if the user is assigned to the current user
      return currentUser.userId === Number(targetUserId) ||
             currentUser.uuid === targetUserId;

    default:
      return false;
  }
};

/**
 * Filter a list of users based on data access level
 */
export const filterUsersByAccess = <T extends { uuid?: string; userId?: number; team_id_fk?: number; department_id_fk?: number }>(
  users: T[]
): T[] => {
  const dataAccess = getDataAccess();

  if (dataAccess === DATA_ACCESS.ALL) {
    return users;
  }

  return users.filter(user =>
    canViewUser(
      user.userId || user.uuid || 0,
      user.team_id_fk,
      user.department_id_fk
    )
  );
};

/**
 * Check if the current user is an admin (Super Admin or System Admin)
 */
export const isAdmin = (): boolean => {
  const role = getCurrentUserRole();
  return role === ROLES.SUPER_ADMIN || role === ROLES.SYSTEM_ADMIN;
};

/**
 * Check if the current user is a manager (Sales Director, Sales Manager, or Team Lead)
 */
export const isManager = (): boolean => {
  const role = getCurrentUserRole();
  return role === ROLES.SALES_DIRECTOR ||
         role === ROLES.SALES_MANAGER ||
         role === ROLES.TEAM_LEAD;
};

/**
 * Check if the current user has read-only access
 */
export const isReadOnly = (): boolean => {
  const role = getCurrentUserRole();
  return role === ROLES.READ_ONLY;
};

/**
 * Check if the current user should see team members in the sidebar
 * Based on role hierarchy:
 * - Super Admin / System Admin: See all users (handled by data_access)
 * - Sales Director: See all users (handled by data_access)
 * - Sales Manager: See users in their department (handled by data_access)
 * - Team Lead: See users in their team (handled by data_access)
 * - Sales Representatives: Don't show team members section
 */
export const shouldShowTeamMembers = (): boolean => {
  const dataAccess = getDataAccess();

  // Show team members if user has team, department, or all access
  // Don't show if user only has "own" or "assigned" access
  return dataAccess !== DATA_ACCESS.OWN && dataAccess !== DATA_ACCESS.ASSIGNED;
};
